<template>
  <div
    v-if="loaded"
    class="bg-white rounded-xl relative w-full overflow-hidden hover:shadow transition-all duration-300 ease-in-out z-20 flex flex-col"
  >
    <!-- Pro plan overlay -->
    <div
      v-if="showUpgrade"
      class="absolute inset-0 z-30 opacity-30 bg-black dj_overlay"
    ></div>
    <!-- /Pro plan overlay -->

    <!-- Card image -->
    <div
      :style="{ backgroundImage: 'url(' + page.image_featured + ')' }"
      class="bg-cover bg-center relative h-48 w-full text-white flex items-center justify-center flex-col"
    ></div>
    <!-- /Card Image -->

    <!-- Card Content -->
    <div class="relative p-6 flex flex-col justify-between">
      <div class="pb-6">
        <h3 class="mb-6">{{ page.title }}</h3>
        <h5 v-if="page.description">{{ page.description }}</h5>
      </div>
    </div>
    <!-- /Card Content -->

    <div class="flex flex-col mt-auto mx-6 border-t border-truegray-300">
      <!-- Author -->
      <div class="py-3">
        <div class="flex space-x-2 items-center w-3/4">
          <div
            class="rounded-full flex items-center justify-center border-2 border-truegray-100 w-12 h-12 relative"
          >
            <div
              v-if="page.author.logo"
              class="bg-no-repeat bg-cover bg-center w-8 h-8 rounded-full"
              :style="{ backgroundImage: 'url(' + page.author.logo + ')' }"
            ></div>
            <div
              v-else
              class="flex justify-center items-center w-8 h-8 rounded-full text-cw-900"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-8 w-8"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
          </div>
          <div class="flex flex-col justify-center">
            <h6 class="text-cw-alt">Content brought to you by:</h6>
            <h6
              v-if="page.author.title"
              :style="{ maxWidth: '180px' }"
              class="text-sm text-truegray-500 truncate overflow-ellipsis"
            >
              {{ page.author.title }}
            </h6>
          </div>
        </div>
      </div>
      <!-- /Author -->

      <!-- Card base -->
      <div class="mt-auto border-t border-truegray-300 pt-3">
        <h6 v-if="page.statistics.shares > 1" class="text-truegray-400">
          {{ page.statistics.shares }} schools have shared this page
        </h6>
        <h6 v-if="page.statistics.shares === 1" class="text-truegray-400">
          1 school has shared this page
        </h6>
        <h6 v-if="page.statistics.shares === 0" class="text-truegray-400">
          No schools have shared this page yet
        </h6>
      </div>
    </div>

    <!-- Buttons -->
    <div v-if="showUpgrade" class="relative z-40">
      <button
        class="btn btn-cw-900 h-16 w-full mb-0 rounded-none rounded-b-xl"
        @click="handleUpgrade"
      >
        <div class="ml-8 mr-8 flex flex-col">
          <h6>Pro plan only.</h6>
          <h6 class="underline">Upgrade</h6>
        </div>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="1.5"
          stroke="currentColor"
          class="w-8 h-6"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"
          />
        </svg>
      </button>
    </div>
    <div v-else>
      <div class="flex flex-wrap border-truegray-300 mt-auto border-top w-full">
        <div
          class="flex flex-col items-center justify-center space-y-2 text-truegray-500 h-16 w-1/2 cursor-pointer hover:bg-truegray-100 transition-all duration-100"
          @click="viewpage"
        >
          <a
            ref="viewpage"
            :href="settings.cloud_domain + '/cloudpost/' + page.id"
            target="_blank"
            class="hidden"
          ></a>
          <h6>Preview</h6>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-5 w-5"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
            <path
              fill-rule="evenodd"
              d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
              clip-rule="evenodd"
            />
          </svg>
        </div>
        <div
          v-if="settings.audience < 1"
          class="flex flex-col items-center justify-center space-y-2 text-white h-16 w-1/2 bg-cw-900 cursor-pointer transition-all duration-100 px-4"
          @click="goToSettings()"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <h6><span>Update Number of students to save to pages</span></h6>
        </div>
        <button
          v-else
          class="flex flex-col items-center justify-center space-y-2 text-white h-16 w-1/2 hover:bg-cw-900 cursor-pointer transition-all duration-100"
          :class="[
            saved || thinking || settings.tenant_id === page.tenant_id
              ? 'bg-cw-900'
              : 'bg-cw-300',
          ]"
          :disabled="settings.tenant_id === page.tenant_id || thinking"
          @click="saveCloudpage(page.id, page.tenant_id)"
        >
          <lottie v-if="thinking" :options="loadingAnimationOptions" />
          <h6>
            <span v-if="settings.tenant_id === page.tenant_id && !thinking"
              >pageed by you</span
            ><span
              v-if="
                !saved && !thinking && settings.tenant_id !== page.tenant_id
              "
              >Save to pages</span
            ><span
              v-if="saved && !thinking && settings.tenant_id !== page.tenant_id"
              >Saved</span
            >
          </h6>
          <svg
            v-if="!saved && !thinking && settings.tenant_id !== page.tenant_id"
            xmlns="http://www.w3.org/2000/svg"
            class="h-5 w-5"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              d="M7.707 10.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V6h5a2 2 0 012 2v7a2 2 0 01-2 2H4a2 2 0 01-2-2V8a2 2 0 012-2h5v5.586l-1.293-1.293zM9 4a1 1 0 012 0v2H9V4z"
            />
          </svg>
          <svg
            v-if="
              (saved ||
                (settings.tenant_id === page.tenant_id &&
                  page.author.type === 1)) &&
              !thinking
            "
            xmlns="http://www.w3.org/2000/svg"
            class="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M5 13l4 4L19 7"
            />
          </svg>
        </button>
      </div>
    </div>
    <!-- /Buttons -->
  </div>
</template>

<script>
import { EventBus } from '../../../../../js/plugins/event-bus.js'
import { mapGetters } from 'vuex'
import * as loadingAnimation from '../../../../../shared/components/loading.json'

export default {
  props: {
    page: { default: false },
  },

  data() {
    return {
      loadingAnimationOptions: { animationData: loadingAnimation.default },
      loadingAnimate: false,
      start_date_short: null,
      end_date_short: null,
      saved: false,
      loaded: false,
      thinking: false,
    }
  },

  computed: {
    // Set all map getters
    ...mapGetters({
      settings: 'shared/getSettings',
      tenantId: 'shared/getTenantId',
      savedCloudPosts: 'careerwise/getSavedCloudposts',
    }),

    showUpgrade() {
      // tenant is not pro AND post author is not Careerwise
      return (
        !this.settings?.isPro &&
        this.page.author?.id > this.settings?.cw_organisation_id
      )
    },
  },

  watch: {
    savedCloudPosts: {
      handler: function (list) {
        if (list.includes(this.page.id)) {
          this.$set(this, 'saved', true)
        } else {
          this.$set(this, 'saved', false)
        }
      },

      deep: true,
    },
  },

  mounted() {
    if (this.page) {
      this.$set(this, 'loaded', true)
      if (this.savedCloudPosts.includes(this.page.id)) {
        this.$set(this, 'saved', true)
      } else {
        this.$set(this, 'saved', false)
      }
    }
  },

  methods: {
    /**
     * Save cloud page to pages
     */
    saveCloudpage(id, tenant_id) {
      if (tenant_id === this.tenantId || this.saved) {
        return
      }
      this.$set(this, 'thinking', true)
      let submitData = {}
      submitData.post_id = id
      submitData.publish = true

      // Get this page...
      axios
        .post('/api/auth/manager-cloud-post-save', submitData)
        .then((response) => {
          EventBus.$emit('CloneResponseReceived')
          let result = response.data
          if (result.success) {
            this.$set(this, 'thinking', false)
            this.$set(this, 'saved', true)
            //  remove pages from visited so new drafts reflect
            // Update the saved list:
            if (result.saved) {
              this.$store.commit(
                'careerwise/UPDATE_SAVED_CLOUDPOSTS',
                result.saved,
              )
            }
            EventBus.$emit('reloadWebsiteSettings')
          } else {
            EventBus.$emit('triggerAlertResponse', {
              status: 'error',
              message: 'Failed to save page',
            })
          }
        })
    },

    goToSettings() {
      this.$router.push({ name: 'Settings' })
    },

    viewpage() {
      this.$refs.viewpage.click()
    },

    handleUpgrade() {
      EventBus.$emit('showModal', {
        action: 'cw-registration',
        data: {
          reason: 3,
        },
      })
    },
  },
}
</script>

<style scoped>
.saved-modal {
  background: rgba(255, 255, 255, 0.95);
}
</style>
